<template>
  <v-container class="ma-0 pa-0" fluid>
    <v-container fluid class="px-6 py-6 full-height-container">
      <v-row class="fill-height">
        <!-- LEFT SIDE: GENERATE SECTION -->
        <v-col cols="12" md="5" lg="4">
          <v-card outlined class="pa-6" style="height: 100%;">
            <div class="text-h5 font-weight-bold mb-2">Generate Report</div>
            <div class="text-body-2 text--secondary mb-6">
              Select an ad group and generate a new Active Future TPR Pivot
            </div>

            <v-autocomplete
              v-model="selectedAdGroup"
              :items="userAdGroups"
              item-text="name"
              item-value="id"
              outlined
              return-object
              dense
              background-color="input"
              label="Ad Group"
              class="mb-4"
              @change="onSelectionChange"
            ></v-autocomplete>

            <v-btn
              block
              large
              color="primary"
              class="text-none font-weight-bold"
              elevation="2"
              @click="createExport"
              :disabled="!selectedAdGroup || isLoading || hasActiveReports"
              :loading="isGenerating"
            >
              <v-icon left>mdi-file-chart-outline</v-icon>
              Generate Report
            </v-btn>

            <v-divider class="my-6"></v-divider>

            <div class="text-caption text--secondary" v-if="hasActiveReports">
              <v-icon small color="orange">mdi-timer-sand</v-icon>
              A report is currently being generated. Please wait for it to complete.
            </div>
            <div class="text-caption text--secondary" v-else>
              <v-icon small color="grey">mdi-information-outline</v-icon>
              Allow the system a few seconds to initialize your report request. Report generation can take up to an hour.
            </div>
          </v-card>
        </v-col>

        <!-- RIGHT SIDE: STATUS TABLE -->
        <v-col cols="12" md="7" lg="8" class="d-flex">
          <v-card outlined class="flex-card d-flex flex-column">
            <v-card-title class="d-flex align-center pb-2">
              <span class="text-h6 font-weight-bold">Report Status</span>
              <v-spacer></v-spacer>
              <v-chip
                small
                outlined
                color="primary"
                class="mr-3"
                v-if="exportStatuses.length > 0"
              >
                {{ exportStatuses.length }} {{ exportStatuses.length === 1 ? 'report' : 'reports' }}
              </v-chip>
              <v-btn
                icon
                small
                :disabled="isLoading"
                @click="loadExportStatuses()"
                :loading="isRefreshing"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-card-title>

            <v-divider></v-divider>

            <v-data-table
              :headers="exportStatusHeaders"
              :items="exportStatuses"
              :loading="isLoading"
              class="elevation-0 flex-table"
              disable-sort
              dense
              :height="tableHeight"
              fixed-header
            >
              <template v-slot:loading>
                <v-sheet class="text-center pa-8" color="transparent">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="40"
                    class="mb-4"
                  ></v-progress-circular>
                  <div class="text-body-2 text--secondary">Loading reports...</div>
                </v-sheet>
              </template>

              <template v-slot:item.adGroupName="{ item }">
                <div class="font-weight-medium">
                  {{ item.adGroupName }}
                </div>
              </template>

              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.status)"
                  :text-color="getStatusTextColor(item.status)"
                  class="font-weight-medium"
                >
                  <v-icon left small v-if="item.status === 'INITIALIZED'">mdi-clock-outline</v-icon>
                  <v-icon left small v-else-if="item.status === 'PROCESSING'">mdi-loading mdi-spin</v-icon>
                  <v-icon left small v-else-if="item.status === 'PROCESSED'">mdi-check-circle</v-icon>
                  <v-icon left small v-else-if="item.status === 'FAILED'">mdi-alert-circle</v-icon>
                  {{ item.status }}
                </v-chip>
                <v-btn
                  v-if="item.status === 'FAILED' && !hasActiveReports"
                  x-small
                  color="error"
                  text
                  class="text-none ml-2"
                  @click="retryExport(item)"
                >
                  <v-icon left x-small>mdi-refresh</v-icon>
                  Retry
                </v-btn>
              </template>

              <template v-slot:item.timestamp="{ item }">
                <div class="text-body-2">{{ formatTimestamp(item.timestamp) }}</div>
                <div class="text-caption text--secondary" v-if="item.status === 'INITIALIZED' || item.status === 'PROCESSING'">
                  {{ getElapsedTime(item.timestamp) }}
                </div>
              </template>

              <template v-slot:no-data>
                <v-sheet class="text-center pa-12" color="transparent">
                  <v-icon size="64" color="grey lighten-1" class="mb-4">
                    mdi-file-document-outline
                  </v-icon>
                  <div class="text-h6 text--secondary mb-2">No reports yet</div>
                  <div class="text-body-2 text--secondary">
                    Generate your first report to get started
                  </div>
                </v-sheet>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { fullwidth } from "@/mixins/fullwidth"
import { paging } from "@/mixins/paging"
import { displayHelpers } from "@/mixins/displayHelpers"
import { userAccess } from "@/mixins/user-access"
import { utils } from "@/mixins/utils"
import { shared } from "@/mixins/shared"
import Items from '@/axios/items';

export default {
  name: "Export20",
  mixins: [
    fullwidth,
    displayHelpers,
    paging,
    userAccess,
    utils,
    shared
  ],
  data() {
    return {
      selectedAdGroup: null,
      isLoading: false,
      isGenerating: false,
      isRefreshing: false,
      exportStatusHeaders: [
        { text: "Ad Group", value: "adGroupName", sortable: false, class: 'accent white--text' },
        { text: "Status", value: "status", sortable: false, class: 'accent white--text' },
        { text: "Requested", value: "timestamp", sortable: false, class: 'accent white--text' }
      ],
      exportStatuses: [],
      refreshInterval: null,
      tableHeight: 'calc(100vh - 300px)'
    }
  },
  created() {
    if (!this.canAccessExport20) {
      this.$router.replace({ name: 'home' })
      this.$store.dispatch('setSnackbar', {
        status: 'error',
        text: 'You do not have access to this page'
      })
    }
  },
  mounted() {
    if (!this.canAccessExport20) return

    this.initializeData()
    this.refreshInterval = setInterval(() => {
      if (this.hasActiveReports) {
        this.loadExportStatuses(true)
      }
    }, 2500)
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  computed: {
    canAccessExport20() {
        return this.$auth.tenant === 'alliance-retail-group' &&
              ['tpr-manager.admin', 'arg-admin', 'arg-category-manager', 'arg-group-manager', 'arg-support'].some(role => this.userGroups.includes(role))
    },
    hasActiveReports() {
      return this.exportStatuses.some(status => 
        status.status === 'PROCESSING' || status.status === 'INITIALIZED'
      )
    }
  },
  watch: {
    userAdGroups: {
      handler(newValue) {
        if (newValue?.length > 0 && !sessionStorage.export_20_ad_group) {
          this.selectedAdGroup = newValue[0]
        }
      },
      deep: true
    },
    selectedAdGroup(newValue) {
      if (newValue) {
        this.saveState()
        this.loadExportStatuses()
      }
    }
  },
  methods: {
    saveState() {
      if (this.selectedAdGroup) {
        sessionStorage.setItem(
          "export_20_ad_group",
          JSON.stringify(this.selectedAdGroup)
        )
      }
    },

    async onSelectionChange() {
      this.isLoading = true
      try {
        this.saveState()
        await this.loadExportStatuses()
      } catch (err) {
        console.error("Error during selection change:", err)
      } finally {
        this.isLoading = false
      }
    },

    async initializeData() {
      try {
        this.isLoading = true
        const savedAdGroup = sessionStorage.getItem("export_20_ad_group")
        this.selectedAdGroup = savedAdGroup
          ? JSON.parse(savedAdGroup)
          : this.userAdGroups[0]

        this.saveState()
        await this.loadExportStatuses()
      } catch (err) {
        console.error("Error initializing data:", err)
      } finally {
        this.isLoading = false
      }
    },

    async createExport() {
      if (!this.selectedAdGroup) return

      this.isGenerating = true
      try {
        await Items.triggerExport20(
          this.selectedAdGroup.id,
          this.selectedAdGroup.name
        )

        this.$store.dispatch('setSnackbar', {
          status: 'success',
          text: 'Report generation started'
        })
        this.$gtag.event('export_create')

        // Immediately refresh to pick up new INITIALIZED status
        // The API now creates the record before returning, so it should be available
        await this.loadExportStatuses()
      } catch (err) {
        console.error("Error creating export:", err)
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Failed to start report generation'
        })
      } finally {
        this.isGenerating = false
      }
    },

    async loadExportStatuses(silent = false) {
      if (!this.selectedAdGroup?.id) return

      try {
        if (silent) {
          this.isRefreshing = true
        } else {
          this.isLoading = true
        }

        const res = await Items.getExport20Status()
        
        this.exportStatuses = res.data.map(item => ({
          id: item.id,
          adGroupId: item.ad_group_id,
          adGroupName: item.ad_group_name,
          status: item.status,
          timestamp: item.created_at,
          downloadUrl: item.url
        }))
      } catch (err) {
        console.error("Error loading export statuses:", err)
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Failed to load export statuses'
        })
      } finally {
        this.isLoading = false
        this.isRefreshing = false
      }
    },

    async retryExport(item) {
      if (!item) return

      this.isGenerating = true
      try {
        await Items.triggerExport20(
          item.adGroupId,
          item.adGroupName
        )

        this.$store.dispatch('setSnackbar', {
          status: 'success',
          text: `Retrying report for ${item.adGroupName}`
        })
        this.$gtag.event('export_retry')

        // Immediately refresh to pick up new INITIALIZED status
        await this.loadExportStatuses()
      } catch (err) {
        console.error("Error retrying export:", err)
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Failed to retry report generation'
        })
      } finally {
        this.isGenerating = false
      }
    },

    getStatusColor(status) {
      const colors = {
        'INITIALIZED': 'blue lighten-4',
        'PROCESSING': 'orange lighten-4',
        'PROCESSED': 'green lighten-4',
        'FAILED': 'red lighten-4'
      }
      return colors[status] || 'grey lighten-3'
    },

    getStatusTextColor(status) {
      const colors = {
        'INITIALIZED': 'blue darken-3',
        'PROCESSING': 'orange darken-3',
        'PROCESSED': 'green darken-3',
        'FAILED': 'red darken-3'
      }
      return colors[status] || 'grey darken-2'
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '—'
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    },

    getElapsedTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      
      if (diffMins < 1) return 'Just started'
      if (diffMins < 60) return `${diffMins} min elapsed`
      const diffHours = Math.floor(diffMins / 60)
      const remainingMins = diffMins % 60
      return `${diffHours}h ${remainingMins}m elapsed`
    }
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table-header th {
  font-weight: 600 !important;
}

.mdi-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.full-height-container {
  min-height: calc(100vh - 64px);
}

.fill-height {
  height: 100%;
}

.flex-card {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.flex-table {
  flex: 1;
  overflow-y: auto;
}
</style>